import React from 'react';

import {useGate, useUnit} from 'effector-react';
import {useTranslation} from 'react-i18next';

import {MetricCard, MetricCardGrid} from '@/widgets/MetricCard';
import {ManagementSummaryGate, managementSummaryModel} from '@entities/management-summary/model';
import {CURRENCY} from '@invoice/core/constants/currency';
import {BlockWrapper, SummaryWrapper} from '@pages/Dashboard/styled';
import {YearLineChart} from '@pages/OfficerSummary/ui/YearLineChart';
import {Spinner} from '@shared/ui/Spinner';
import {Title} from '@shared/ui/Title';
import {handleNil} from '@utils/handleNil';
import {formatNumberWithFraction} from '@utils/number';

import {Chart, GapValue, SpinnerOverlay, SummaryContent} from './styled';

const formatCount = (value?: number | null) =>
    handleNil(value, (number) => number.toLocaleString('en-US', {maximumFractionDigits: 0}));

const formatAmount = (value?: number | null) => handleNil(value, formatNumberWithFraction);

const formatPercent = (value?: number | null) =>
    handleNil(value, (number) => `${formatNumberWithFraction(number)}%`);

const metricGridStyles = {
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    padding: 0,
    boxShadow: 'none',
    background: 'transparent',
};

export const ManagementSummary = () => {
    const {t} = useTranslation();
    const {state} = useUnit(managementSummaryModel);
    const {data, pending} = state;

    useGate(ManagementSummaryGate);

    const basicInvoiceCount =
        (data?.invoices.total ?? 0) -
        (data?.invoices.annulling ?? 0) -
        (data?.invoices.corrective ?? 0);
    const chartValues = React.useMemo(
        () =>
            data?.balances.year
                ? Object.values(data.balances.year).map((balance) => balance?.saldo ?? null)
                : [],
        [data?.balances.year],
    );

    return (
        <SummaryContent>
            {pending && (
                <SpinnerOverlay>
                    <Spinner size={3} color="primary" />
                </SpinnerOverlay>
            )}

            <SummaryWrapper>
                <BlockWrapper style={{gridColumn: '1 / -1'}}>
                    <MetricCardGrid style={metricGridStyles}>
                        <MetricCard
                            label={t('page_main.persons.reg')}
                            value={formatCount(data?.users.total)}
                        />
                        <MetricCard
                            label={t('page_main.persons.active')}
                            value={formatCount(data?.users.active)}
                        />
                    </MetricCardGrid>
                </BlockWrapper>

                <BlockWrapper>
                    <Title size={4}>{t('page_main.declarations.title')}</Title>
                    <MetricCardGrid style={metricGridStyles}>
                        <MetricCard
                            label={t('page_main.declarations.filled')}
                            value={formatCount(data?.declarations.filled)}
                        />
                        <MetricCard
                            label={t('page_main.declarations.zero')}
                            value={formatCount(data?.declarations.zero)}
                        />
                        <MetricCard
                            label={t('page_main.declarations.to_pay')}
                            value={formatCount(data?.declarations.pay)}
                        />
                        <MetricCard
                            label={t('page_main.declarations.to_return')}
                            value={formatCount(data?.declarations.return)}
                        />
                    </MetricCardGrid>
                </BlockWrapper>

                <BlockWrapper>
                    <Title size={4}>{t('page_main.invoices.title')}</Title>
                    <MetricCardGrid style={metricGridStyles}>
                        <MetricCard
                            label={t('page_main.invoices.total')}
                            value={formatCount(data?.invoices.total)}
                        />
                        <MetricCard
                            label={t('page_main.invoices.basic')}
                            value={formatCount(basicInvoiceCount)}
                        />
                        <MetricCard
                            label={t('page_main.invoices.annulling')}
                            value={formatCount(data?.invoices.annulling)}
                        />
                        <MetricCard
                            label={t('page_main.invoices.corrective')}
                            value={formatCount(data?.invoices.corrective)}
                        />
                    </MetricCardGrid>
                </BlockWrapper>

                <BlockWrapper style={{gridColumn: '1 / -1'}}>
                    <Title size={4}>{t('page_main.gapes.title')}</Title>
                    <MetricCardGrid
                        style={{
                            ...metricGridStyles,
                            gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))',
                        }}
                    >
                        <MetricCard
                            label={t('page_main.gapes.total_count')}
                            value={formatCount(data?.gapes.totalCnt?.cnt)}
                        />
                        <MetricCard
                            label={t('page_main.gapes.total_sum')}
                            value={formatAmount(data?.gapes.totalSum?.sum)}
                        />
                        <MetricCard
                            label={t('page_main.gapes.percents')}
                            value={formatPercent(data?.gapes.deductionPercent?.percent)}
                        />
                        <MetricCard
                            label={t('page_main.gapes.not_filed', {currency: CURRENCY})}
                            value={
                                <GapValue>
                                    <span>{formatCount(data?.gapes.notFiledCnt?.cnt)}</span>
                                    <span>{formatAmount(data?.gapes.notFiledCnt?.sum)}</span>
                                </GapValue>
                            }
                        />
                        <MetricCard
                            label={t('page_main.gapes.differ', {currency: CURRENCY})}
                            value={
                                <GapValue>
                                    <span>{formatCount(data?.gapes.differCntSum?.cnt)}</span>
                                    <span>{formatAmount(data?.gapes.differCntSum?.sum)}</span>
                                </GapValue>
                            }
                        />
                        <MetricCard
                            label={t('page_main.gapes.not_paid', {currency: CURRENCY})}
                            value={
                                <GapValue>
                                    <span>{formatCount(data?.gapes.notPaidCntSum?.cnt)}</span>
                                    <span>{formatAmount(data?.gapes.notPaidCntSum?.sum)}</span>
                                </GapValue>
                            }
                        />
                    </MetricCardGrid>
                </BlockWrapper>

                <BlockWrapper>
                    <Title size={4}>{t('page_main.management.balance.title')}</Title>
                    <MetricCardGrid style={metricGridStyles}>
                        <MetricCard
                            label={t('page_main.management.balance.total')}
                            value={formatAmount(data?.balances.period?.saldo)}
                        />
                        <MetricCard
                            label={t('page_main.management.balance.accrual')}
                            value={formatAmount(data?.balances.period?.accrual)}
                        />
                        <MetricCard
                            label={t('page_main.management.balance.refund')}
                            value={formatAmount(data?.balances.period?.refund)}
                        />
                        <MetricCard
                            label={t('page_main.management.balance.payed')}
                            value={formatAmount(data?.balances.period?.payed)}
                        />
                    </MetricCardGrid>
                    <Chart>
                        <YearLineChart
                            values={chartValues}
                            label={t('page_main.management.balance.total')}
                        />
                    </Chart>
                </BlockWrapper>
            </SummaryWrapper>
        </SummaryContent>
    );
};
